import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { X, UserCircle2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { getMyProfile } from "@/lib/profile.functions";
import { useAuth } from "@/lib/auth-context";

const DISMISS_KEY = "wtb_profile_banner_dismissed_v1";

type MissingField = "display_name" | "country" | "avatar_url" | "bio" | "tcg_specialisations";

const LABELS: Record<MissingField, string> = {
  display_name: "display name",
  country: "country",
  avatar_url: "profile picture",
  bio: "bio",
  tcg_specialisations: "TCGs you trade",
};

export function ProfileCompletionBanner() {
  const { user, loading } = useAuth();
  const fetchProfile = useServerFn(getMyProfile);
  const [missing, setMissing] = useState<MissingField[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    }
  }, []);

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;
    fetchProfile()
      .then((p) => {
        if (cancelled || !p) return;
        const m: MissingField[] = [];
        if (!p.display_name) m.push("display_name");
        if (!p.country) m.push("country");
        if (!p.avatar_url) m.push("avatar_url");
        if (!p.bio) m.push("bio");
        if (!p.tcg_specialisations || p.tcg_specialisations.length === 0) m.push("tcg_specialisations");
        setMissing(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loading, user, fetchProfile]);

  if (dismissed || missing.length === 0) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mb-5 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <UserCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
      <div className="flex-1 text-sm">
        <p className="font-semibold text-foreground">Complete your profile</p>
        <p className="mt-0.5 text-muted-foreground">
          Add your {missing.map((m) => LABELS[m]).join(", ")} so other traders can find and trust you.
        </p>
        <Link to="/account" className="mt-2 inline-block text-sm font-semibold text-primary hover:underline">
          Finish profile →
        </Link>
      </div>
      <button
        onClick={dismiss}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
